import { Button } from "../ui/button";
import { axiosInstance } from "@/pages/auth/auth.context";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EventPosition } from "@/pages/EventDetails";
import { SectionHeader, Figure } from "@/components/brand/SectionHeader";
import { StatusPill } from "@/components/brand/StatusPill";
import { Check, LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/pages/auth/auth.context";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface EventPositionsSummaryProps {
  eventPositions: EventPosition[];
  eventId: string;
}

export default function EventPositionsSummary({
  eventPositions,
  eventId,
}: EventPositionsSummaryProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [localEventPositions, setLocalEventPositions] =
    useState<EventPosition[]>(eventPositions);
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });
  const queryClient = useQueryClient();

  const closeErrorDialog = () => {
    setErrorDialog({ open: false, message: "" });
  };

  const sortedPositions = [...localEventPositions].sort((a, b) =>
    a.tableName.localeCompare(b.tableName),
  );

  async function SignUp(eventPositionId: string): Promise<void> {
    setLoading((prev) => ({ ...prev, [eventPositionId]: true }));
    try {
      const response = await axiosInstance.post(
        `userevent/signup/${eventPositionId}`,
      );
      if (response.status === 200 || response.status === 201) {
        toast({
          title: "Success",
          description: "You have successfully been signed up for the event",
          variant: "success",
        });

        // Optimistically update the UI
        if (user) {
          setLocalEventPositions((prev) =>
            prev.map((pos) => {
              if (pos.eventPositionId === eventPositionId) {
                return {
                  ...pos,
                  signedUpUsers: [
                    ...pos.signedUpUsers,
                    {
                      id: user.id,
                      firstName: user.firstName,
                      lastName: user.lastName,
                      email: user.email,
                    },
                  ],
                };
              }
              return pos;
            }),
          );
        }

        // Invalidate the query to refresh data in the background
        queryClient.invalidateQueries({ queryKey: ["event"] });
        queryClient.invalidateQueries({ queryKey: ["userPay", eventId] });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Unknown error occurred";
      console.error("Failed to sign user up for event.", errorMessage);
      setErrorDialog({
        open: true,
        message: "Failed to sign up for event. " + errorMessage,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [eventPositionId]: false }));
    }
  }

  async function CancelSignup(eventPositionId: string): Promise<void> {
    setLoading((prev) => ({ ...prev, [eventPositionId]: true }));
    try {
      const response = await axiosInstance.post(
        `userevent/cancel/${eventPositionId}`,
      );
      if (response.status === 200 || response.status === 201) {
        toast({
          title: "Success",
          description: "You have successfully cancelled.",
          variant: "default",
        });

        // Optimistically update the UI
        if (user) {
          setLocalEventPositions((prev) =>
            prev.map((pos) => {
              if (pos.eventPositionId === eventPositionId) {
                return {
                  ...pos,
                  signedUpUsers: pos.signedUpUsers.filter(
                    (signedUser) => signedUser.id !== user.id,
                  ),
                };
              }
              return pos;
            }),
          );
        }

        // Invalidate the query to refresh data in the background
        queryClient.invalidateQueries({ queryKey: ["event"] });
        queryClient.invalidateQueries({ queryKey: ["userPay", eventId] });
      }
    } catch (error) {
      console.error("Failed to cancel event.", error);
      const errorMessage =
        error.response?.data?.error || "Unknown error occurred";

      setErrorDialog({
        open: true,
        message: "Failed to cancel event. " + errorMessage,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [eventPositionId]: false }));
    }
  }

  if (JSON.stringify(eventPositions) !== JSON.stringify(localEventPositions)) {
    setLocalEventPositions(eventPositions);
  }

  const userSignedUpForExclusivePosition = sortedPositions.some(
    (pos) =>
      pos.signedUpUsers.some((signedUpUser) => signedUpUser.id === user?.id) &&
      !pos.allowsMultipleAssignments,
  );

  return (
    <div className="pb-2 pt-5">
      <div className="px-6 pb-1">
        <SectionHeader icon={LayoutGrid}>Available Positions</SectionHeader>
      </div>

      <ul className="mt-3 divide-y divide-border">
        {sortedPositions.map((eventPos) => {
          const totalSlots = eventPos.employeesRequired * eventPos.quantity;
          const filledSlots = eventPos.signedUpUsers.length;
          const hasAvailability = filledSlots < totalSlots;
          const isUserSignedUp = eventPos.signedUpUsers.some(
            (signedUpUser) => signedUpUser.id === user?.id,
          );

          const canSignUp =
            eventPos.allowsMultipleAssignments ||
            !userSignedUpForExclusivePosition;

          return (
            <li
              key={eventPos.eventPositionId}
              // The asymmetric padding compensates for the 2px rule, so the
              // text does not shift against the rows above and below.
              className={cn(
                // Stacked below sm. As one wrapping row, a long name pushed
                // the figures onto a second line while a short one did not,
                // so the columns went ragged.
                "row-lift flex flex-col items-start gap-y-2 py-3.5 pr-6 hover:bg-primary/[0.04] sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-3",
                isUserSignedUp
                  ? "border-l-2 border-l-primary bg-primary/[0.05] pl-[22px]"
                  : "pl-6",
              )}
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                <span
                  data-testid="position-name"
                  className="font-medium text-foreground"
                >
                  {eventPos.tableName}
                </span>
                {/* Beside the name rather than in a column: it appears on at
                    most one row, so it cannot make the column ragged. */}
                {isUserSignedUp && (
                  <StatusPill
                    tone="accent"
                    icon={Check}
                    className="shimmer-ring border-transparent"
                  >
                    You are on this
                  </StatusPill>
                )}
              </div>

              <div className="flex w-full items-center gap-4 sm:w-auto sm:gap-5">
                {/* Its own column, so comparing availability down the list is
                    one downward scan rather than hunting left and right after
                    names of different lengths. */}
                <div className="flex w-[76px] shrink-0 justify-end">
                  {hasAvailability ? (
                    <StatusPill tone="warning">
                      {totalSlots - filledSlots} open
                    </StatusPill>
                  ) : (
                    <StatusPill tone="muted">Full</StatusPill>
                  )}
                </div>
                {/* Not formatPay: a pinned test says this is shown exactly
                    as the server sent it, cents and all. */}
                <Figure
                  data-testid="position-pay"
                  className="text-right text-base text-foreground"
                >
                  ${eventPos.employeePay}
                  {eventPos.isFlatRate ? " (flat rate)" : "/hr"}
                </Figure>

                {/* ml-auto while stacked, so the action is in the same place
                    on every row rather than trailing the pay figure. */}
                <div className="ml-auto w-[104px] shrink-0 sm:ml-0">
                  {hasAvailability &&
                    !isUserSignedUp &&
                    eventPos.userEligible &&
                    canSignUp && (
                      <Button
                        variant="cta"
                        onClick={() => SignUp(eventPos.eventPositionId)}
                        disabled={loading[eventPos.eventPositionId]}
                        size="sm"
                        className="w-full"
                      >
                        {loading[eventPos.eventPositionId]
                          ? "Processing..."
                          : "Sign Up"}
                      </Button>
                    )}
                  {isUserSignedUp && (
                    // Outlined, not filled: dropping a shift is routine and
                    // reversible, and does not warrant the loudest colour in
                    // the palette.
                    <Button
                      variant="outline"
                      onClick={() => CancelSignup(eventPos.eventPositionId)}
                      disabled={loading[eventPos.eventPositionId]}
                      size="sm"
                      className="w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      {loading[eventPos.eventPositionId]
                        ? "Processing..."
                        : "Cancel"}
                    </Button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <AlertDialog
        open={errorDialog.open}
        onOpenChange={(open) => !open && closeErrorDialog()}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Error
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {errorDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={closeErrorDialog}
              className="bg-destructive hover:bg-destructive/90"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
